import { useState, type Key } from 'react';
import {
  Table,
  Button,
  Typography,
  Space,
  Modal,
  Form,
  Input,
  AutoComplete,
  App,
  Popconfirm,
  Drawer,
  Checkbox,
  Alert,
  Tag,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ContactGroupMeta, GroupContact } from '@bom/shared';
import { fmtTime } from '../../lib/format';
import { useRoleLabelOptions } from '../../api/role-labels';
import { api, ApiError } from '../../lib/apiClient';
import { ExcelImportButton } from '../../components/ExcelImportButton';
import {
  parseContactSheet,
  parseGroupFullSheet,
  GROUP_CONTACT_TEMPLATE_HEADERS,
  GROUP_CONTACT_TEMPLATE_SAMPLE,
  GROUP_FULL_TEMPLATE_HEADERS,
  GROUP_FULL_TEMPLATE_SAMPLE,
  type ContactImportRow,
  type GroupFullImportRow,
} from '../../lib/excel';
import {
  useContactGroups,
  useContactGroup,
  useCreateContactGroup,
  useUpdateContactGroup,
  useDeleteContactGroup,
  useAddGroupContact,
  useUpdateGroupContact,
  useDeleteGroupContact,
} from '../../api/contact-groups';

/** 管理某分组内联系人的抽屉 */
function GroupContactsDrawer({ gid, onClose }: { gid?: string | undefined; onClose: () => void }) {
  const { message } = App.useApp();
  const q = useContactGroup(gid);
  const add = useAddGroupContact(gid ?? '');
  const upd = useUpdateGroupContact(gid ?? '');
  const del = useDeleteGroupContact(gid ?? '');
  const [form] = Form.useForm();
  const [editing, setEditing] = useState<GroupContact | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [sync, setSync] = useState(false); // 本次改动是否同步到已导入产品
  const roleOptions = useRoleLabelOptions();

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    setAddOpen(true);
  };
  const openEdit = (c: GroupContact) => {
    setEditing(c);
    form.setFieldsValue({ name: c.name, role_label: c.role_label, note: c.note });
    setAddOpen(true);
  };

  const onSubmit = () =>
    form.validateFields().then((v) => {
      if (editing) {
        upd.mutate(
          { cid: editing.id, body: { ...v, sync } },
          {
            onSuccess: (r) => {
              message.success(sync ? `已更新，同步 ${r.synced} 个产品` : '已更新');
              setAddOpen(false);
            },
            onError: (e) => message.error((e as Error).message || '更新失败'),
          },
        );
      } else {
        add.mutate(
          { ...v, sync },
          {
            onSuccess: (r) => {
              message.success(sync ? `已添加，同步 ${r.synced} 个产品` : '已添加');
              setAddOpen(false);
            },
            onError: (e) => message.error((e as Error).message || '添加失败'),
          },
        );
      }
    });

  return (
    <Drawer
      title={q.data ? `分组联系人 · ${q.data.group.name}（${q.data.group.company}）` : '分组联系人'}
      open={!!gid}
      onClose={onClose}
      width={720}
      loading={q.isLoading}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message="同步开关"
          description="勾选后，对本分组联系人的增/改/删会同步到已「从本分组导入」的产品；不勾选只改分组、不影响产品。"
        />
        <Space style={{ justifyContent: 'space-between', width: '100%' }}>
          <Checkbox checked={sync} onChange={(e) => setSync(e.target.checked)}>
            本次改动同步到已导入本分组的产品
          </Checkbox>
          <Space size="small">
            <ExcelImportButton<ContactImportRow>
              templateName="联系人分组导入模板"
              templateHeaders={GROUP_CONTACT_TEMPLATE_HEADERS}
              templateSample={GROUP_CONTACT_TEMPLATE_SAMPLE}
              parse={(wb, s) => parseContactSheet(wb, s, false)}
              importOne={async (row) => {
                await add.mutateAsync({
                  email: row.email,
                  name: row.name,
                  sync,
                  ...(row.role_label ? { role_label: row.role_label } : {}),
                  ...(row.note ? { note: row.note } : {}),
                });
              }}
              onDone={() => q.refetch()}
              label="导入联系人"
              previewColumns={[
                { title: '邮箱', dataIndex: 'email' },
                { title: '姓名', dataIndex: 'name' },
                { title: '角色标签', dataIndex: 'role_label' },
                { title: '备注', dataIndex: 'note' },
              ]}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
              添加联系人
            </Button>
          </Space>
        </Space>

        <Table<GroupContact>
          rowKey="id"
          size="small"
          pagination={false}
          dataSource={q.data?.contacts ?? []}
          columns={[
            { title: '姓名', dataIndex: 'name', width: 130 },
            { title: '邮箱', dataIndex: 'email' },
            { title: '角色标签', dataIndex: 'role_label', width: 110 },
            { title: '备注', dataIndex: 'note', render: (v?: string) => v ?? '—' },
            {
              title: '操作',
              key: 'action',
              width: 130,
              render: (_: unknown, c: GroupContact) => (
                <Space size="small">
                  <Button size="small" type="link" onClick={() => openEdit(c)}>
                    编辑
                  </Button>
                  <Popconfirm
                    title="删除该联系人？"
                    description={sync ? '将同步从已导入产品删除' : '仅从分组删除'}
                    okText="删除"
                    okButtonProps={{ danger: true }}
                    cancelText="取消"
                    onConfirm={() =>
                      del.mutate(
                        { cid: c.id, sync },
                        {
                          onSuccess: (r) =>
                            message.success(sync ? `已删除，同步 ${r.synced} 个产品` : '已删除'),
                          onError: (e) => message.error((e as Error).message || '删除失败'),
                        },
                      )
                    }
                  >
                    <Button size="small" type="link" danger>
                      删除
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Space>

      <Modal
        title={editing ? '编辑联系人' : '添加联系人'}
        open={addOpen}
        onOk={onSubmit}
        confirmLoading={add.isPending || upd.isPending}
        onCancel={() => setAddOpen(false)}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          {!editing && (
            <Form.Item
              name="email"
              label="邮箱"
              rules={[{ required: true, type: 'email', message: '请输入有效邮箱' }]}
            >
              <Input placeholder="contact@company.com" />
            </Form.Item>
          )}
          <Form.Item name="name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="role_label" label="角色标签（选填）">
            <AutoComplete
              options={roleOptions}
              placeholder="选择或输入，如 采购 / 品管"
              filterOption={(input, opt) => (opt?.value ?? '').includes(input)}
            />
          </Form.Item>
          <Form.Item name="note" label="备注">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </Drawer>
  );
}

/** 展开行：只读预览该分组的联系人（展开时才拉取） */
function GroupContactsPreview({ gid }: { gid: string }) {
  const q = useContactGroup(gid);
  return (
    <Table<GroupContact>
      rowKey="id"
      size="small"
      loading={q.isLoading}
      pagination={false}
      dataSource={q.data?.contacts ?? []}
      locale={{ emptyText: '该分组暂无联系人' }}
      columns={[
        { title: '姓名', dataIndex: 'name', width: 140 },
        { title: '邮箱', dataIndex: 'email' },
        {
          title: '角色标签',
          dataIndex: 'role_label',
          width: 140,
          render: (v?: string) => (v ? <Tag>{v}</Tag> : '—'),
        },
        { title: '备注', dataIndex: 'note', render: (v?: string) => v ?? '—' },
      ]}
    />
  );
}

export function ContactGroupsPage() {
  const { message } = App.useApp();
  const groups = useContactGroups();
  const create = useCreateContactGroup();
  const update = useUpdateContactGroup();
  const del = useDeleteContactGroup();
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [createOpen, setCreateOpen] = useState(false);
  const [editGroup, setEditGroup] = useState<ContactGroupMeta | null>(null);
  const [manageGid, setManageGid] = useState<string | undefined>();
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [delDone, setDelDone] = useState(0);

  const onBulkDelete = async () => {
    setBulkDeleting(true);
    setDelDone(0);
    let done = 0;
    const results = await Promise.allSettled(
      selectedKeys.map((gid) =>
        del.mutateAsync(gid).finally(() => {
          done += 1;
          setDelDone(done);
        }),
      ),
    );
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const fail = results.length - ok;
    setBulkDeleting(false);
    setSelectedKeys([]);
    if (fail === 0) message.success(`已删除 ${ok} 个分组`);
    else message.warning(`删除 ${ok} 个，${fail} 个失败`);
  };

  const onCreate = () =>
    form.validateFields().then((v) =>
      create.mutate(v, {
        onSuccess: () => {
          message.success('分组已创建');
          setCreateOpen(false);
          form.resetFields();
        },
        onError: (e) => message.error((e as Error).message || '创建失败'),
      }),
    );

  const onEdit = () =>
    editForm.validateFields().then((v) => {
      if (!editGroup) return;
      update.mutate(
        { gid: editGroup.id, body: v },
        {
          onSuccess: () => {
            message.success('已更新');
            setEditGroup(null);
          },
          onError: (e) => message.error((e as Error).message || '更新失败'),
        },
      );
    });

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          联系人分组
        </Typography.Title>
        <Space>
          {selectedKeys.length > 0 && (
            <Popconfirm
              title={`删除选中的 ${selectedKeys.length} 个分组？`}
              description="将连同分组下的联系人一并删除，不可恢复（不影响已导入产品的联系人）"
              okText="删除"
              okButtonProps={{ danger: true }}
              cancelText="取消"
              onConfirm={onBulkDelete}
            >
              <Button danger loading={bulkDeleting}>
                {bulkDeleting
                  ? `删除中 ${delDone}/${selectedKeys.length}`
                  : `批量删除 (${selectedKeys.length})`}
              </Button>
            </Popconfirm>
          )}
          <ExcelImportButton<GroupFullImportRow, Map<string, string>>
            templateName="联系人分组导入模板"
            templateHeaders={GROUP_FULL_TEMPLATE_HEADERS}
            templateSample={GROUP_FULL_TEMPLATE_SAMPLE}
            parse={parseGroupFullSheet}
            prepare={() => new Map<string, string>()}
            importOne={async (row, cache) => {
              const key = `${row.group_name.toLowerCase()}|${row.company.toLowerCase()}`;
              let gid = cache.get(key);
              if (!gid) {
                const existing = (groups.data ?? []).find(
                  (g) =>
                    String(g.name).trim().toLowerCase() === row.group_name.toLowerCase() &&
                    String(g.company).trim().toLowerCase() === row.company.toLowerCase(),
                );
                gid =
                  existing?.id ??
                  (
                    await api.post<{ id: string }>('/contact-groups', {
                      name: row.group_name,
                      company: row.company,
                    })
                  ).id;
                cache.set(key, gid);
              }
              await api.post(`/contact-groups/${gid}/contacts`, {
                email: row.email,
                name: row.name,
                ...(row.role_label ? { role_label: row.role_label } : {}),
                ...(row.note ? { note: row.note } : {}),
              });
            }}
            onDone={() => groups.refetch()}
            label="导入分组"
            previewColumns={[
              { title: '分组名', dataIndex: 'group_name' },
              { title: '公司', dataIndex: 'company' },
              { title: '邮箱', dataIndex: 'email' },
              { title: '姓名', dataIndex: 'name' },
              { title: '角色标签', dataIndex: 'role_label' },
              { title: '备注', dataIndex: 'note' },
            ]}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            新建分组
          </Button>
        </Space>
      </Space>
      <Typography.Text type="secondary">
        按公司维护联系人分组；产品可在「成员/联系人 →
        通知联系人」从分组一键导入。可用「导入分组」上传一张含 分组名/公司/邮箱/姓名
        的表，按需自动建组并加入联系人。
      </Typography.Text>

      <Table<ContactGroupMeta>
        rowKey="id"
        loading={groups.isLoading}
        dataSource={groups.data ?? []}
        pagination={false}
        rowSelection={{
          selectedRowKeys: selectedKeys,
          onChange: (keys: Key[]) => setSelectedKeys(keys.map(String)),
        }}
        expandable={{
          expandedRowRender: (g) => <GroupContactsPreview gid={g.id} />,
          rowExpandable: (g) => (g.contact_count ?? 0) > 0,
        }}
        columns={[
          { title: '分组名', dataIndex: 'name', width: 180 },
          {
            title: '公司',
            dataIndex: 'company',
            width: 180,
            render: (v: string) => <Tag>{v}</Tag>,
          },
          {
            title: '联系人数',
            dataIndex: 'contact_count',
            width: 100,
            render: (v?: number) => v ?? 0,
          },
          { title: '备注', dataIndex: 'note', render: (v?: string) => v ?? '—' },
          { title: '更新时间', dataIndex: 'updated_at', width: 170, render: fmtTime },
          {
            title: '操作',
            key: 'action',
            width: 200,
            render: (_: unknown, g: ContactGroupMeta) => (
              <Space size="small">
                <Button size="small" type="link" onClick={() => setManageGid(g.id)}>
                  联系人
                </Button>
                <Button
                  size="small"
                  type="link"
                  onClick={() => {
                    setEditGroup(g);
                    editForm.setFieldsValue({ name: g.name, company: g.company, note: g.note });
                  }}
                >
                  编辑
                </Button>
                <Popconfirm
                  title={`删除分组「${g.name}」？`}
                  description="仅删除分组本身，不影响已导入到各产品的联系人"
                  okText="删除"
                  okButtonProps={{ danger: true }}
                  cancelText="取消"
                  onConfirm={() =>
                    del.mutate(g.id, {
                      onSuccess: () => message.success('已删除'),
                      onError: (e) =>
                        message.error(
                          `删除失败：${e instanceof ApiError ? e.message : (e as Error).message}`,
                        ),
                    })
                  }
                >
                  <Button size="small" type="link" danger>
                    删除
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title="新建联系人分组"
        open={createOpen}
        onOk={onCreate}
        confirmLoading={create.isPending}
        onCancel={() => setCreateOpen(false)}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="name"
            label="分组名"
            rules={[{ required: true, message: '请输入分组名' }]}
          >
            <Input placeholder="如 玖光-采购组" />
          </Form.Item>
          <Form.Item
            name="company"
            label="公司"
            rules={[{ required: true, message: '请输入公司' }]}
          >
            <Input placeholder="如 玖光" />
          </Form.Item>
          <Form.Item name="note" label="备注">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`编辑分组 ${editGroup?.name ?? ''}`}
        open={!!editGroup}
        onOk={onEdit}
        confirmLoading={update.isPending}
        onCancel={() => setEditGroup(null)}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" preserve={false}>
          <Form.Item
            name="name"
            label="分组名"
            rules={[{ required: true, message: '请输入分组名' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="company"
            label="公司"
            rules={[{ required: true, message: '请输入公司' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="note" label="备注">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <GroupContactsDrawer gid={manageGid} onClose={() => setManageGid(undefined)} />
    </Space>
  );
}
